<script setup lang="ts">
import type { ThemeValues } from "./utils"
import footerLogo from "@brand-logo-footer"
import { computed, watch } from "vue"
import { getBrandName } from "@/components/Footer/footer.constants"
import { Card, Icon, Input, ReadMore } from "@/components/ui"
import { useTranslation } from "@/composables"
import ButtonPreview from "./ButtonPreview.vue"
import { defaultTheme } from "./constants/presetThemes"

const props = defineProps<{ values: ThemeValues }>()

const { t } = useTranslation()

const brand = getBrandName()

const baseFontPath = computed(() => `https://fonts.googleapis.com/css2?family=${props.values?.baseFont?.value ?? "Montserrat"}&display=swap`)
const headingFontPath = computed(
  () => `https://fonts.googleapis.com/css2?family=${props.values?.headingFont?.value ?? "Comfortaa"}&display=swap`,
)
const baseFontLabel = computed(() => props.values?.baseFont?.label ?? "Montserrat")
const headingFontLabel = computed(() => props.values?.headingFont?.label ?? "Comfortaa")

watch(props.values, () => {
  const rootEl: HTMLElement | null = document.querySelector(":root")
  rootEl?.style.setProperty("--template-header-background-preview", props.values.headerBackground ?? defaultTheme.colors.headerBackground)
  rootEl?.style.setProperty("--template-footer-background-preview", props.values.footerBackground ?? defaultTheme.colors.footerBackground)
  rootEl?.style.setProperty("--template-primary-colour-preview", props.values.primaryColour ?? defaultTheme.colors.primaryColour)
  rootEl?.style.setProperty("--template-secondary-colour-preview", props.values.secondaryColour ?? defaultTheme.colors.secondaryColour)
  rootEl?.style.setProperty("--template-link-text-preview", props.values.linkText ?? defaultTheme.colors.linkText)
  rootEl?.style.setProperty("--template-nav-text-preview", props.values.navText ?? defaultTheme.colors.navText)
  rootEl?.style.setProperty("--template-primary-button-text-preview", props.values.primaryButtonText ?? defaultTheme.colors.primaryButtonText)
  rootEl?.style.setProperty("--template-secondary-button-text-preview", props.values.secondaryButtonText ?? defaultTheme.colors.secondaryButtonText)
  rootEl?.style.setProperty("--template-primary-card-text-preview", props.values.primaryCardText ?? defaultTheme.colors.primaryCardText)
  rootEl?.style.setProperty("--template-secondary-card-text-preview", props.values.secondaryCardText ?? defaultTheme.colors.secondaryCardText)
  rootEl?.style.setProperty("--template-border-preview", props.values.borders ?? defaultTheme.colors.borders)
})
</script>

<template>
  <div>
    <h2 class="font-comfortaa font-bold text-xl flex gap-2 items-center mb-6">
      <Icon icon="EyeOn" class="text-gpx-pink" />
      {{ t('theme_selector.preview') }}
    </h2>
    <div class="bg-gray-50 rounded-xl p-4">
      <div class="gpx-theme-preview border bg-white">
        <link :href="baseFontPath" rel="stylesheet">
        <link :href="headingFontPath" rel="stylesheet">
        <div class="gpx-theme-preview-nav container">
          <h3 class="text-2xl">
            {{ t('theme_selector.theme_preview.title') }}
          </h3>
          <div class="gap-8 hidden xl:flex">
            <a class="gpx-preview-nav-item" href="#">{{ t('theme_selector.theme_preview.welcome') }}</a>
            <a class="gpx-preview-nav-item" href="#">{{ t('theme_selector.theme_preview.add_photos_videos') }}</a>
          </div>
          <Icon icon="CircleMenu" class="mobile-menu-preview xl:hidden" />
        </div>
        <div class="container p-6 flex flex-col gap-8">
          <div>
            <div class="text-xl">
              {{ t('theme_selector.theme_preview.buttons') }}
            </div>
            <div class="flex flex-col xl:flex-row gap-2 flex-wrap mt-4">
              <ButtonPreview variant="primary">
                {{ t('theme_selector.theme_preview.primary_button') }}
              </ButtonPreview>
              <ButtonPreview variant="secondary">
                {{ t('theme_selector.theme_preview.secondary_button') }}
              </ButtonPreview>
              <ButtonPreview variant="tertiary">
                {{ t('theme_selector.theme_preview.tertiary_button') }}
              </ButtonPreview>
              <ButtonPreview variant="flat">
                {{ t('theme_selector.theme_preview.flat_button') }}
              </ButtonPreview>
            </div>
          </div>

          <div>
            <div class="text-xl">
              {{ t('theme_selector.theme_preview.tiles') }}
            </div>
            <div class="flex gap-2 flex-wrap mt-4">
              <Card class="flex-1" variant="primary">
                <template #header>
                  <h3>{{ t('theme_selector.theme_preview.primary_tile') }}</h3>
                </template>
                <p class="mt-4">
                  Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et
                  dolore magna aliqua.
                </p>
                <div class="flex flex-col gap-6 mt-8">
                  <ButtonPreview variant="secondary">
                    {{ t('theme_selector.theme_preview.secondary_button') }}
                  </ButtonPreview>
                </div>
              </Card>
              <Card class="flex-1" variant="secondary">
                <template #header>
                  <h3>{{ t('theme_selector.theme_preview.secondary_tile') }}</h3>
                </template>
                <p class="mt-4">
                  Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et
                  dolore magna aliqua.
                </p>
                <div class="flex flex-col gap-6 mt-8">
                  <ButtonPreview variant="primary">
                    {{ t('theme_selector.theme_preview.primary_button') }}
                  </ButtonPreview>
                </div>
              </Card>
            </div>
          </div>
          <hr style="width: 100%">
          <div>
            <div class="text-xl">
              {{ t('theme_selector.theme_preview.login_example') }}
            </div>
            <br>
            <Card variant="secondary" class="gpx-login-card overflow-hidden" :with-padding="false">
              <div class="grid xl:grid-cols-2">
                <div class="gpx-login-card-form p-8">
                  <h1 class="gpx-login-title_desktop text-2xl" style="text-align: left; padding-bottom: 1.5rem">
                    {{ t('theme_selector.theme_preview.title') }}
                  </h1>
                  <h2 class="text-xl">
                    {{ t('theme_selector.theme_preview.message_from_the_host') }}
                  </h2>
                  <br>
                  <ReadMore
                    class="text-sm"
                    content="Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt."
                  />
                  <div>
                    <div class="mt-8 flex flex-col gap-4">
                      <Input name="name" label="Full name*" />
                      <ButtonPreview class="xl:w-fit">
                        {{ t('theme_selector.theme_preview.continue') }}
                      </ButtonPreview>
                    </div>
                  </div>
                  <div class="mt-8 text-xs">
                    <span>
                      {{ t('theme_selector.theme_preview.use_of_platform') }}
                    </span>
                  </div>
                </div>
                <div
                  class="gpx-login-image hidden xl:flex"
                  style="background-image: var(--gpx-default-cover-image)"
                >
                  <p class="text-black">
                    {{ t('theme_selector.theme_preview.event_image') }}
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </div>
        <div class="footer-preview w-full flex justify-between flex-wrap-reverse gap-10 xl:p-4 pb-4">
          <div class="w-full flex flex-col xl:w-fit xl:items-start items-center">
            <img :src="footerLogo" :alt="t('footer.logo_alt', { brand })" class="max-w-40 max-h-32">
            <div class="flex gap-4 mt-8">
              <Icon icon="Instagram" />
              <Icon icon="Facebook" />
              <Icon icon="Pinterest" />
              <Icon icon="TikTok" />
              <Icon icon="YouTube" />
            </div>
          </div>
          <div class="flex gap-10 w-full xl:w-fit justify-center xl:justify-start">
            <div class="flex flex-col gap-3">
              <div class="font-semibold">
                {{ t('footer.learn_more_title') }}
              </div>
              <div>{{ t('footer.host_login') }}</div>
              <div>{{ t('footer.get_guestpix') }}</div>
              <div>{{ t('footer.faqs') }}</div>
              <div>{{ t('footer.pricing') }}</div>
            </div>
            <div class="flex flex-col gap-3">
              <div class="font-semibold">
                {{ t('footer.legal_title') }}
              </div>
              <div>{{ t('footer.privacy') }}</div>
              <div>{{ t('footer.terms_of_service') }}</div>
              <div>{{ t('footer.host_tcs') }}</div>
              <div>{{ t('footer.acceptable_use_policy') }}</div>
            </div>
          </div>
          <div />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.gpx-theme-preview {
  flex-direction: column;
}

.gpx-theme-preview-nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 75px;
  background: var(--template-header-background-preview);
  border-bottom: var(--template-border-preview);
  color: var(--template-nav-text-preview);
}

.gpx-preview-nav-item {
  color: var(--template-nav-text-preview, #072a47);
  font-weight: 500;
  font-size: 1rem;
  text-decoration: none;
  transition: color 0.1s ease-in;
}

.gpx-preview-nav-item:hover,
.gpx-preview-nav-item:focus {
  text-decoration: underline;
}

.gpx-login-image {
  background-size: cover;
  background-position: center;
  height: 100%;
  width: auto;
  object-fit: cover;
  filter: grayscale(1);
  align-items: center;
  justify-content: center;
  color: var(--template-primary-colour-preview);
}

.gpx-login-card-form h1,
.gpx-login-card-form h2 {
  color: var(--template-secondary-card-text-preview);
}

.footer-preview {
  background-color: var(--template-footer-background-preview, #ddd);
  color: var(--template-link-text-preview, #072a47);
}

.mobile-menu-preview {
  border: 1px solid var(--template-nav-text-preview, #072a47);
  width: 2.3rem;
  height: 2.3rem;
  border-radius: 100%;
}

.gpx-card_primary {
  background-color: var(--template-primary-colour-preview);
  color: var(--template-primary-card-text-preview);
}

.gpx-card_secondary {
  background-color: var(--template-secondary-colour-preview);
  color: var(--template-secondary-card-text-preview);
  border: var(--template-border-preview);
}

@media (width >= 768px) {
  .gpx-theme-preview {
    display: flex;
  }
}
</style>

<style>
.gpx-theme-preview h1,
.gpx-theme-preview h2,
.gpx-theme-preview h3,
.gpx-theme-preview h4,
.gpx-theme-preview h5,
.gpx-theme-preview h6 {
  font-family: v-bind("headingFontLabel"), "Open Sans", Montserrat, sans-serif !important;
  margin: 0;
  padding: 0;
}

.gpx-theme-preview * {
  font-family: v-bind("baseFontLabel"), "Open Sans", Montserrat, sans-serif !important;
}
</style>
